import { libraryHref, resolveLibraryAddress } from './libraryAddress'

/**
 * ADR-106 Decision 1 — the six stops of the swipe chain, left to right. Both
 * tab levels of the Library are flattened into the one sequence (Q1).
 */
export const SWIPE_CHAIN = [
  'home',
  'mine',
  'catalog-decks',
  'catalog-courses',
  'catalog-explore',
  'progress',
] as const

export type ChainStop = (typeof SWIPE_CHAIN)[number]

const IN_LIBRARY: ReadonlySet<ChainStop> = new Set(['mine', 'catalog-decks', 'catalog-courses', 'catalog-explore'])

/** Which chain stop, if any, the location is showing. Only the three chain paths qualify (Decision 3.2). */
export function chainStopAt(pathname: string, search: string): ChainStop | null {
  if (pathname === '/home') return 'home'
  if (pathname === '/progress') return 'progress'
  if (pathname === '/library') {
    const { seg, sub } = resolveLibraryAddress(new URLSearchParams(search))
    return seg === 'mine' ? 'mine' : `catalog-${sub ?? 'decks'}` as ChainStop
  }
  return null
}

export interface ChainMove {
  to: ChainStop
  url: string
  /** Q3: moving within the Library replaces the history entry; crossing between screens adds one. */
  replace: boolean
}

/**
 * The move a swipe makes from `from` — exactly one stop along the chain, to
 * the chain neighbour, never wrapping (Decision 7). `direction` +1 is the next
 * stop to the right (a finger moving right to left), −1 the one to the left.
 *
 * The URL is the one the equivalent tap uses (Decision 8), so arriving by
 * swipe is arriving by tap: crossing into the Library from Home is the bottom
 * navigation's `/library`; moving to Mine inside the Library is the segment
 * tab's `?seg=mine`; a Catalog sub-tab is its own address (P1). A swipe always
 * goes to the neighbour — Progress back is Catalog › Explore (Q2) and Mine
 * forward is Catalog › Decks — whatever sub-tab a tap on Catalog would
 * remember (P4).
 */
export function chainMove(from: ChainStop, direction: 1 | -1): ChainMove | null {
  const i = SWIPE_CHAIN.indexOf(from) + direction
  if (i < 0 || i >= SWIPE_CHAIN.length) return null
  const to = SWIPE_CHAIN[i]
  const replace = IN_LIBRARY.has(from) && IN_LIBRARY.has(to)
  return { to, url: urlFor(to, replace), replace }
}

/**
 * Which part of the screen moves between two stops (revised Q6): only what
 * differs. Between Catalog sub-tabs the Catalog heading and sub-tab strip
 * stay; between Mine and Catalog the segment row stays; between top-level
 * screens everything above the bottom navigation moves.
 */
export type SwipeRegion = 'app' | 'library' | 'catalog'

export function swipeRegion(from: ChainStop, to: ChainStop | null): SwipeRegion {
  if (to === null) return from === 'home' || from === 'progress' ? 'app' : regionAlone(from)
  if (from.startsWith('catalog-') && to.startsWith('catalog-')) return 'catalog'
  if (IN_LIBRARY.has(from) && IN_LIBRARY.has(to)) return 'library'
  return 'app'
}

/** The region a drag at an end of the chain moves (the ends are Home and Progress). */
function regionAlone(stop: ChainStop): SwipeRegion {
  return IN_LIBRARY.has(stop) ? 'library' : 'app'
}

function urlFor(stop: ChainStop, withinLibrary: boolean): string {
  switch (stop) {
    case 'home': return '/home'
    case 'progress': return '/progress'
    case 'mine': return withinLibrary ? libraryHref('mine') : '/library'
    case 'catalog-decks': return libraryHref('catalog', 'decks')
    case 'catalog-courses': return libraryHref('catalog', 'courses')
    case 'catalog-explore': return libraryHref('catalog', 'explore')
  }
}
